import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { Text } from "~/components/Text";

type EventMoment = {
  id: string;
  imageUrl: string;
  caption: string;
};

type EventMomentsCarouselProps = {
  moments: EventMoment[];
};

export function EventMomentsCarousel({ moments }: EventMomentsCarouselProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    containScroll: "trimSnaps",
    loop: false,
  });
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  useEffect(() => {
    if (!emblaApi) return;
    const update = () => {
      setCanPrev(emblaApi.canScrollPrev());
      setCanNext(emblaApi.canScrollNext());
    };
    update();
    emblaApi.on("select", update);
    emblaApi.on("reInit", update);
    return () => {
      emblaApi.off("select", update);
      emblaApi.off("reInit", update);
    };
  }, [emblaApi]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  if (moments.length === 0) return null;

  return (
    <div className="relative">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex gap-3">
          {moments.map((moment) => (
            <div
              key={moment.id}
              className="relative flex-[0_0_60%] sm:flex-[0_0_45%] lg:flex-[0_0_40%] aspect-[4/5] overflow-hidden rounded-lg bg-butter-cream"
            >
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${moment.imageUrl})` }}
                role="img"
                aria-label={moment.caption || "Event moment"}
              />
              {moment.caption && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-butter-green/90 to-transparent p-3">
                  <Text
                    as="span"
                    variant="secondary"
                    size="xs"
                    color="light"
                    className="line-clamp-2"
                  >
                    {moment.caption}
                  </Text>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {(canPrev || canNext) && (
        <>
          <button
            type="button"
            aria-label="Previous moment"
            onClick={scrollPrev}
            disabled={!canPrev}
            className="absolute -left-2 top-1/2 -translate-y-1/2 hidden h-8 w-8 items-center justify-center rounded-full bg-butter-cream-light shadow-md transition-opacity hover:opacity-80 disabled:opacity-40 sm:flex"
          >
            <FiChevronLeft size={18} className="text-butter-green" aria-hidden />
          </button>
          <button
            type="button"
            aria-label="Next moment"
            onClick={scrollNext}
            disabled={!canNext}
            className="absolute -right-2 top-1/2 -translate-y-1/2 hidden h-8 w-8 items-center justify-center rounded-full bg-butter-cream-light shadow-md transition-opacity hover:opacity-80 disabled:opacity-40 sm:flex"
          >
            <FiChevronRight size={18} className="text-butter-green" aria-hidden />
          </button>
        </>
      )}
    </div>
  );
}
